import type { TableCardConfig } from '../models/table-config.model';
import { isSchemaTimeDimensionField } from '../components/editors/shared/data-tab/chart-data-tab-axis.util';
import { randomHex } from './random-hex.util';
import { visualizationRecord } from './table-presentation-visualization.util';

/** Table display columns exclude schema date/time fields (handled by `timeDimension`). */
export function isTableDisplayDimensionColumn(col: {
	name?: string;
	dataType?: string;
}): boolean {
	const name = col.name?.trim() ?? '';
	if (!name) {
		return true;
	}
	return !isSchemaTimeDimensionField({
		name,
		dataType: col.dataType,
	});
}

export type TableColumnRole = 'dimension' | 'metric';

/** New card-scoped column id when the user adds a dimension or metric row. */
export function createTableColumnId(role: TableColumnRole): string {
	const hex = randomHex(4);
	return role === 'metric' ? `metric_${hex}` : `dimension_${hex}`;
}

export interface ConfiguredTableColumnField {
	id: string;
	name: string;
	label?: string;
}

/** Ordered dimension/metric fields from card config (dimensions first, then metrics). */
function configuredTableDataColumnFieldsInDataOrder(
	config: TableCardConfig
): ConfiguredTableColumnField[] {
	const fields: ConfiguredTableColumnField[] = [];
	const push = (
		id: string | undefined,
		name: string | undefined,
		label: string | undefined
	) => {
		const fieldName = name?.trim();
		if (!fieldName) {
			return;
		}
		fields.push({
			id: id?.trim() ?? '',
			name: fieldName,
			label: label?.trim() || undefined,
		});
	};
	for (const d of config.data?.dimensions ?? []) {
		push(d?.id, d?.name, d?.label);
	}
	for (const m of config.data?.metrics ?? []) {
		push(m?.id, m?.name, m?.label);
	}
	return fields;
}

/**
 * Display order of column ids from `visualization.columnOrder`.
 * Falls back to data dimensions-then-metrics order when unset.
 */
export function resolveTableColumnDisplayOrderIds(
	config: TableCardConfig
): string[] {
	const fields = configuredTableDataColumnFieldsInDataOrder(config);
	const defaultOrder = fields
		.map(field => field.id.trim())
		.filter(id => id.length > 0);
	const fromViz = visualizationRecord(config.visualization)['columnOrder'];
	if (!Array.isArray(fromViz) || fromViz.length === 0) {
		return defaultOrder;
	}
	const allowed = new Set(defaultOrder);
	const ordered: string[] = [];
	const seen = new Set<string>();
	for (const entry of fromViz) {
		if (typeof entry !== 'string') {
			continue;
		}
		const id = entry.trim();
		if (!id || !allowed.has(id) || seen.has(id)) {
			continue;
		}
		ordered.push(id);
		seen.add(id);
	}
	for (const id of defaultOrder) {
		if (!seen.has(id)) {
			ordered.push(id);
		}
	}
	return ordered;
}

/** Persist display order on visualization without clearing column overrides. */
export function mergeTableColumnOrderIntoVisualization(
	visualization: unknown,
	columnOrder: string[]
): Record<string, unknown> {
	const base = visualizationRecord(visualization);
	const cleaned = columnOrder
		.map(id => id.trim())
		.filter((id, index, all) => id.length > 0 && all.indexOf(id) === index);
	return { ...base, columnOrder: cleaned };
}

/**
 * Configured fields in grid/Data-tab display order.
 * Query shape stays in `data.dimensions` / `data.metrics`; presentation order is
 * `visualization.columnOrder`.
 */
export function configuredTableDataColumnFields(
	config: TableCardConfig
): ConfiguredTableColumnField[] {
	const fields = configuredTableDataColumnFieldsInDataOrder(config);
	const order = resolveTableColumnDisplayOrderIds(config);
	if (order.length === 0 || fields.length === 0) {
		return fields;
	}
	const byId = new Map(
		fields
			.filter(field => field.id.trim().length > 0)
			.map(field => [field.id.trim(), field] as const)
	);
	const ordered: ConfiguredTableColumnField[] = [];
	const used = new Set<string>();
	for (const id of order) {
		const field = byId.get(id);
		if (field) {
			ordered.push(field);
			used.add(id);
		}
	}
	for (const field of fields) {
		const id = field.id.trim();
		if (!id || used.has(id)) {
			continue;
		}
		ordered.push(field);
	}
	return ordered;
}

/** True when the card has at least one dimension or metric field name configured. */
export function tableCardHasConfiguredDisplayColumns(
	config: TableCardConfig | null | undefined
): boolean {
	if (!config) {
		return false;
	}
	return configuredTableDataColumnFields(config).length > 0;
}

/** Whether a getCardData `columnDefs[].id` matches a configured dimension/metric. */
export function tableColumnDefIdMatchesConfiguredField(
	columnDefId: string,
	field: ConfiguredTableColumnField
): boolean {
	const defId = columnDefId.trim();
	if (!defId) {
		return false;
	}
	const fieldId = field.id.trim();
	const fieldName = field.name.trim();
	if (
		defId === fieldId ||
		defId === fieldName ||
		defId.toLowerCase() === fieldId.toLowerCase() ||
		defId.toLowerCase() === fieldName.toLowerCase()
	) {
		return true;
	}
	const segment = fieldName.split('.').pop();
	if (segment && defId.toLowerCase() === segment.toLowerCase()) {
		return true;
	}
	return fieldName.toLowerCase().endsWith(`.${defId.toLowerCase()}`);
}

const TABLE_COMPARISON_COLUMN_SUFFIX = '__prev';

export function tableComparisonPrevColumnId(metricColumnId: string): string {
	return `${metricColumnId.trim()}${TABLE_COMPARISON_COLUMN_SUFFIX}`;
}

function tableComparisonBaseColumnId(comparisonColumnId: string): string {
	const id = comparisonColumnId.trim();
	if (!id.endsWith(TABLE_COMPARISON_COLUMN_SUFFIX)) {
		return id;
	}
	return id.slice(0, -TABLE_COMPARISON_COLUMN_SUFFIX.length);
}

export function isTableComparisonColumnId(columnId: string): boolean {
	return columnId.trim().endsWith(TABLE_COMPARISON_COLUMN_SUFFIX);
}

/** True when `sessions__prev` belongs to configured metric `sessions`. */
export function tableColumnDefIdMatchesComparisonForField(
	columnDefId: string,
	field: ConfiguredTableColumnField
): boolean {
	if (!isTableComparisonColumnId(columnDefId)) {
		return false;
	}
	const baseId = columnDefId
		.trim()
		.slice(0, -TABLE_COMPARISON_COLUMN_SUFFIX.length);
	if (!baseId) {
		return false;
	}
	return tableColumnDefIdMatchesConfiguredField(baseId, field);
}

export function isConfiguredTableMetricField(
	config: TableCardConfig,
	field: ConfiguredTableColumnField
): boolean {
	const fieldName = field.name.trim();
	const fieldId = field.id.trim();
	return (config.data?.metrics ?? []).some(m => {
		const name = m?.name?.trim() ?? '';
		const id = m?.id?.trim() ?? '';
		return (
			(fieldName && (name === fieldName || id === fieldId)) ||
			(fieldId && id === fieldId)
		);
	});
}

function tableComparisonEnabled(config: TableCardConfig): boolean {
	return config.data?.comparison?.enabled === true;
}

/**
 * Keep only column defs for configured dimensions/metrics, in config order.
 * Backend `columnDefs[].id` is often the schema field key (e.g. `addressLines`), not `dimension_*`.
 */
export function scopeTableColumnDefsToConfiguredData<T extends { id: string }>(
	config: TableCardConfig,
	columnDefs: T[]
): T[] {
	const configured = configuredTableDataColumnFields(config);
	if (configured.length === 0) {
		return [];
	}
	const includeComparison = tableComparisonEnabled(config);
	const remaining = [...columnDefs];
	const scoped: T[] = [];
	for (const field of configured) {
		const index = remaining.findIndex(def =>
			tableColumnDefIdMatchesConfiguredField(def.id, field)
		);
		if (index >= 0) {
			scoped.push(remaining[index]!);
			remaining.splice(index, 1);
		}
		// `__prev` columns stay in columnar row data for stacked in-cell comparison
		// but are not registered as visible grid columns (stable column count).
		if (includeComparison && isConfiguredTableMetricField(config, field)) {
			const prevIndex = remaining.findIndex(def =>
				tableColumnDefIdMatchesComparisonForField(def.id, field)
			);
			if (prevIndex >= 0) {
				remaining.splice(prevIndex, 1);
			}
		}
	}
	return scoped;
}

type TableColumnDefSeed = {
	id: string;
	headerName?: string;
	type?: string;
};

/**
 * Ensures every configured dimension/metric has a grid column def, in config order.
 * Grid Pro remote `getValue(columnId)` requires `column.id` to equal a key in the
 * columnar payload — align def ids to those keys when they differ (e.g. schema path
 * vs `dimension_*`).
 */
export function ensureConfiguredTableColumnDefs<T extends TableColumnDefSeed>(
	config: TableCardConfig,
	columnDefs: T[],
	columnarKeys: string[] = []
): T[] {
	const configured = configuredTableDataColumnFields(config);
	if (configured.length === 0) {
		return [];
	}
	const scoped = scopeTableColumnDefsToConfiguredData(config, columnDefs);
	const remainingKeys = [...columnarKeys];
	const out: T[] = [];
	for (const field of configured) {
		const existing = scoped.find(def =>
			tableColumnDefIdMatchesConfiguredField(def.id, field)
		);
		let dataKey: string | undefined;
		if (remainingKeys.length > 0) {
			const keyIndex = remainingKeys.findIndex(key =>
				tableColumnDefIdMatchesConfiguredField(key, field)
			);
			if (keyIndex >= 0) {
				dataKey = remainingKeys.splice(keyIndex, 1)[0];
			}
		}
		if (existing) {
			const id = dataKey ?? existing.id;
			out.push(id === existing.id ? existing : { ...existing, id });
			continue;
		}
		const id = dataKey ?? (field.name.trim() || field.id.trim());
		if (!id) {
			continue;
		}
		out.push({
			id,
			headerName: field.label?.trim() || field.name,
			type: 'string',
		} as T);
	}
	return out;
}

/** Fingerprint of configured dimension/metric order for grid re-init on Data-tab reorder. */
export function configuredTableFieldsOrderFingerprint(
	config: TableCardConfig | null | undefined
): string {
	if (!config) {
		return '';
	}
	return configuredTableDataColumnFields(config)
		.map(field => `${field.id.trim()}:${field.name.trim()}`)
		.join('>');
}

/** Columnar table payload keyed by field id (getCardData `result.columns`). */
export type TableColumnarCell = string | number | boolean | null;
export type TableColumnarData = Record<string, Array<TableColumnarCell>>;

/** Drop columnar payload keys that are not in configured dimensions/metrics. */
export function filterTableColumnarDataToConfiguredFields(
	config: TableCardConfig,
	columns: TableColumnarData
): TableColumnarData {
	const configured = configuredTableDataColumnFields(config);
	if (configured.length === 0) {
		return {};
	}
	const includeComparison = tableComparisonEnabled(config);
	const keys = Object.keys(columns);
	const out: TableColumnarData = {};
	for (const field of configured) {
		const matchKey = keys.find(key =>
			tableColumnDefIdMatchesConfiguredField(key, field)
		);
		if (matchKey !== undefined && matchKey in columns) {
			out[matchKey] = columns[matchKey]!;
		}
		if (includeComparison && isConfiguredTableMetricField(config, field)) {
			const prevKey = keys.find(key =>
				tableColumnDefIdMatchesComparisonForField(key, field)
			);
			if (prevKey !== undefined && prevKey in columns) {
				out[prevKey] = columns[prevKey]!;
			}
		}
	}
	return out;
}

/** Column ids from config: `data.dimensions` then `data.metrics` (prune viz overrides only). */
export function columnIdsFromTableData(config: TableCardConfig): string[] {
	const ids: string[] = [];
	for (const d of config.data?.dimensions ?? []) {
		if (typeof d?.id === 'string' && d.id.trim()) {
			ids.push(d.id.trim());
		}
	}
	for (const m of config.data?.metrics ?? []) {
		if (typeof m?.id === 'string' && m.id.trim()) {
			ids.push(m.id.trim());
		}
	}
	return ids;
}

/**
 * Display labels from `getCard` `data.metrics` / `data.dimensions` (backend-resolved).
 * Used by table card preview/grid (column id keyed). Not used by Data-tab dropdowns.
 * Read-only — not written back on `updateCard`.
 */
export function tableColumnLabelsByIdFromCardData(
	config: TableCardConfig
): Map<string, string> {
	const labels = new Map<string, string>();
	const register = (key: string | undefined, text: string | undefined) => {
		const colKey = key?.trim();
		const label = text?.trim();
		if (colKey && label) {
			labels.set(colKey, label);
		}
	};
	for (const field of configuredTableDataColumnFields(config)) {
		const text = field.label?.trim();
		if (!text) {
			continue;
		}
		register(field.id, text);
		register(field.name, text);
		const segment = field.name.split('.').pop();
		if (segment) {
			register(segment, text);
		}
	}
	return labels;
}

/**
 * Resolves a Grid column id (often `metric_*` / `dimension_*`) to the Cube member
 * name used in `data.order` / `dataConfig.order`.
 */
export function resolveTableOrderMemberKey(
	config: TableCardConfig,
	columnId: string
): string {
	const id = columnId.trim();
	if (!id) {
		return '';
	}
	const baseId = tableComparisonBaseColumnId(id);
	const fields = configuredTableDataColumnFields(config);
	const match =
		fields.find(f => tableColumnDefIdMatchesConfiguredField(baseId, f)) ??
		fields.find(f => tableColumnDefIdMatchesComparisonForField(id, f));
	const name = match?.name?.trim();
	return name || id;
}

/** Maps getCardData `columnDefs[].id` to schema field `name` from card config. */
export function tableFieldNameByColumnIdFromCardData(
	config: TableCardConfig,
	columnDefs: Array<{ id: string; headerName?: string }>
): Map<string, string> {
	const fields = configuredTableDataColumnFields(config);
	const out = new Map<string, string>();
	for (const def of columnDefs) {
		const match =
			fields.find(f =>
				tableColumnDefIdMatchesConfiguredField(def.id, f)
			) ??
			fields.find(f => {
				const header = def.headerName?.trim().toLowerCase();
				return header && f.name.trim().toLowerCase() === header;
			}) ??
			fields.find(f => {
				const header = def.headerName?.trim().toLowerCase();
				const segment = f.name.split('.').pop()?.toLowerCase();
				return header && segment && header === segment;
			});
		if (match?.name) {
			out.set(def.id, match.name);
		}
	}
	return out;
}

function stripFullTableWidthOverride(
	column: Record<string, unknown>
): Record<string, unknown> {
	const width = column['width'];
	if (width === '100%' || width === 100) {
		const { width: _removed, ...rest } = column;
		// Explicit `auto` so Grid Pro's option diff detects the change from `100%`
		// (omitting `width` does not clear internal column width state on update).
		return { ...rest, width: 'auto' };
	}
	return column;
}

function columnHasExplicitWidth(column: Record<string, unknown>): boolean {
	const width = column['width'];
	return width !== undefined && width !== null && width !== 'auto';
}

/**
 * When every column has a saved width, Grid Pro will not expand the last column to
 * fill the table — leaving empty space on the right in narrower editor previews.
 * Drop the last column width so it absorbs the remainder (distributed mode).
 */
function stripLastColumnWidthWhenAllExplicit(
	columns: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
	if (columns.length < 2) {
		return columns;
	}
	if (!columns.every(columnHasExplicitWidth)) {
		return columns;
	}
	const lastIndex = columns.length - 1;
	const last = columns[lastIndex];
	if (!last || typeof last !== 'object') {
		return columns;
	}
	const { width: _removed, ...rest } = last;
	return [...columns.slice(0, lastIndex), rest];
}

/** Drop `visualization.columns` overrides whose `id` is not in configured data column ids. */
export function pruneVisualizationColumnsToConfiguredIds(
	visualization: Record<string, unknown>,
	allowedIds: string[]
): Record<string, unknown> {
	const allowed = new Set(
		allowedIds.filter(id => typeof id === 'string' && id.trim().length > 0)
	);
	const hasColumns = Array.isArray(visualization['columns']);
	const hasColumnOrder = Array.isArray(visualization['columnOrder']);
	if (allowed.size === 0) {
		if (!hasColumns && !hasColumnOrder) {
			return visualization;
		}
		const next: Record<string, unknown> = { ...visualization };
		if (hasColumns) {
			next['columns'] = [];
		}
		if (hasColumnOrder) {
			next['columnOrder'] = [];
		}
		return next;
	}

	let next: Record<string, unknown> | undefined;

	if (hasColumns) {
		const original = visualization['columns'] as Array<
			Record<string, unknown>
		>;
		let columns = original.filter(entry => {
			const id = entry?.['id'];
			return typeof id === 'string' && allowed.has(id);
		});
		if (allowed.size > 1) {
			columns = columns.map(stripFullTableWidthOverride);
		}
		const unchanged =
			columns.length === original.length &&
			columns.every((col, index) => col === original[index]);
		if (!unchanged) {
			next = { ...(next ?? visualization), columns };
		}
	}

	if (hasColumnOrder) {
		const originalOrder = visualization['columnOrder'] as unknown[];
		const filtered: string[] = [];
		const seen = new Set<string>();
		for (const entry of originalOrder) {
			if (typeof entry !== 'string') {
				continue;
			}
			const id = entry.trim();
			if (!id || !allowed.has(id) || seen.has(id)) {
				continue;
			}
			filtered.push(id);
			seen.add(id);
		}
		for (const id of allowedIds) {
			const trimmed = id.trim();
			if (trimmed && allowed.has(trimmed) && !seen.has(trimmed)) {
				filtered.push(trimmed);
				seen.add(trimmed);
			}
		}
		const orderUnchanged =
			filtered.length === originalOrder.length &&
			filtered.every((id, index) => id === originalOrder[index]);
		if (!orderUnchanged) {
			next = { ...(next ?? visualization), columnOrder: filtered };
		}
	}

	return next ?? visualization;
}

function visualizationOverrideForGridColumn(
	baseId: string,
	overrideById: Map<string, Record<string, unknown>>,
	configuredFields?: ConfiguredTableColumnField[]
): Record<string, unknown> | undefined {
	const direct = overrideById.get(baseId);
	if (direct) {
		return direct;
	}
	if (!configuredFields?.length) {
		return undefined;
	}
	const field = configuredFields.find(entry =>
		tableColumnDefIdMatchesConfiguredField(baseId, entry)
	);
	if (!field) {
		return undefined;
	}
	const fieldId = field.id.trim();
	const fieldName = field.name.trim();
	return (
		(fieldId ? overrideById.get(fieldId) : undefined) ??
		(fieldName ? overrideById.get(fieldName) : undefined)
	);
}

export function mergeVisualizationColumnsById(
	baseColumns: Array<Record<string, unknown>>,
	overrides: Array<Record<string, unknown>>,
	configuredFields?: ConfiguredTableColumnField[]
): Array<Record<string, unknown>> {
	const overrideById = new Map<string, Record<string, unknown>>();
	for (const override of overrides) {
		const id = override['id'];
		if (typeof id !== 'string' || !id.trim()) {
			continue;
		}
		overrideById.set(id.trim(), override);
	}

	return baseColumns.flatMap(base => {
		const baseId = base['id'];
		if (typeof baseId !== 'string' || !baseId.trim()) {
			return [];
		}
		const trimmedId = baseId.trim();
		const override = visualizationOverrideForGridColumn(
			trimmedId,
			overrideById,
			configuredFields
		);
		if (!override) {
			return [base];
		}
		const { id: _overrideId, ...overrideWithoutId } = override;
		return [
			normalizeVisualizationColumnCells(
				deepMergeColumnOptions(base, {
					...overrideWithoutId,
					id: trimmedId,
				})
			),
		];
	});
}

/** Grid Pro uses `cells.format` OR `cells.formatter` OR `cells.renderer` — merged overrides must not keep conflicting keys. */
export function normalizeVisualizationColumnCells(
	column: Record<string, unknown>
): Record<string, unknown> {
	const cells = column['cells'];
	if (!cells || typeof cells !== 'object' || Array.isArray(cells)) {
		return column;
	}
	const cellsRecord = cells as Record<string, unknown>;
	const format = cellsRecord['format'];
	if (typeof format === 'string' && format.trim()) {
		const {
			formatter: _formatter,
			renderer: _renderer,
			...cellsRest
		} = cellsRecord;
		return { ...column, cells: cellsRest };
	}
	const renderer = cellsRecord['renderer'];
	if (renderer && typeof renderer === 'object' && !Array.isArray(renderer)) {
		const {
			formatter: _formatter,
			format: _format,
			...cellsRest
		} = cellsRecord;
		return { ...column, cells: cellsRest };
	}
	return column;
}

/**
 * When only one column is visible, force it to span the table width. Saved pixel/%
 * widths from multi-column layouts otherwise leave empty space on the right.
 *
 * When multiple columns are visible, strip stale `100%` widths (e.g. after removing
 * columns) so Grid Pro can redistribute space instead of overflowing horizontally.
 */
export function normalizeTableGridColumnWidths(
	columns: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
	if (columns.length === 0) {
		return columns;
	}
	if (columns.length === 1) {
		const column = columns[0];
		if (!column || typeof column !== 'object') {
			return columns;
		}
		return [{ ...column, width: '100%' }];
	}
	const stripped = columns.map(column => {
		if (!column || typeof column !== 'object') {
			return column;
		}
		const withoutFullWidth = stripFullTableWidthOverride(column);
		if (columnHasExplicitWidth(withoutFullWidth)) {
			return withoutFullWidth;
		}
		return { ...withoutFullWidth, width: 'auto' };
	});
	return stripLastColumnWidthWhenAllExplicit(stripped);
}

/** @deprecated Use {@link normalizeTableGridColumnWidths}. */
export function applySingleColumnTableGridWidth(
	columns: Array<Record<string, unknown>>
): Array<Record<string, unknown>> {
	return normalizeTableGridColumnWidths(columns);
}

export function mergeColumnOverrideById(
	visualization: unknown,
	columnId: string,
	partial: Record<string, unknown>
): Record<string, unknown> {
	const base = visualizationRecord(visualization);
	const existingColumns = Array.isArray(base['columns'])
		? ([...(base['columns'] as Array<Record<string, unknown>>)] as Array<
				Record<string, unknown>
			>)
		: [];

	const index = existingColumns.findIndex(entry => entry['id'] === columnId);
	const merged = deepMergeColumnOptions(
		index >= 0 ? (existingColumns[index] ?? {}) : { id: columnId },
		{ ...partial, id: columnId }
	);

	if (index >= 0) {
		existingColumns[index] = merged;
	} else {
		existingColumns.push(merged);
	}

	return { ...base, columns: existingColumns };
}

function deepMergeColumnOptions(
	base: Record<string, unknown>,
	override: Record<string, unknown>
): Record<string, unknown> {
	const result: Record<string, unknown> = { ...base };
	for (const key of Object.keys(override)) {
		const baseValue = result[key];
		const incomingValue = override[key];
		if (
			baseValue !== null &&
			typeof baseValue === 'object' &&
			!Array.isArray(baseValue) &&
			incomingValue !== null &&
			typeof incomingValue === 'object' &&
			!Array.isArray(incomingValue)
		) {
			result[key] = deepMergeColumnOptions(
				baseValue as Record<string, unknown>,
				incomingValue as Record<string, unknown>
			);
		} else {
			result[key] = incomingValue;
		}
	}
	return result;
}
